import { useWide } from '../shell/hooks';
import { NeedDot } from './bits';
import { EdgeScroll } from './EdgeScroll';
import { PillMenu } from './PillMenu';

// A page's tabs (Shelves, Queue, My books; consistency pass R9, Kristi Nov 2). On a phone:
// M1 a tab row that doesn't fit 358 px at full names takes a short name per tab (My books:
// Library · Owned · Add · Recent · Review); the full name stays for screen readers and in the
// description below. M2 a row that still scrolls shows the edge (fade and arrows). M3 the chosen
// tab always scrolls fully into view. M4 the needs-you dot sits on the label's top-right corner
// and takes no width; its tip names what needs you. M8 a sixth tab never squeezes in: past five,
// the rest go behind a last More ▾ (a PillMenu). On a laptop: full names, the dot beside the
// label. No counts on tabs (R7 N1): the dot, or nothing.

export interface TabItem<K extends string> {
  key: K;
  name: string;
  /** The phone's short name (M1). */
  short?: string;
  /** Something on this tab needs you: the words after "Needs you: " (R7 N9). */
  need?: string;
}

const MAX_PHONE = 5;

export function TabRow<K extends string>({
  label,
  tabs,
  value,
  onPick,
  narrow: narrowProp,
}: {
  /** The row's name ("My books"). */
  label: string;
  tabs: TabItem<K>[];
  value: K;
  onPick: (k: K) => void;
  /** The phone layout; defaults to the app's (under 700 px). */
  narrow?: boolean;
}) {
  const wide = useWide();
  const narrow = narrowProp ?? !wide;
  const shown = narrow && tabs.length > MAX_PHONE ? tabs.slice(0, MAX_PHONE - 1) : tabs;
  const rest = narrow && tabs.length > MAX_PHONE ? tabs.slice(MAX_PHONE - 1) : [];
  const inRest = rest.find((t) => t.key === value);
  return (
    <EdgeScroll label={`${label} tabs`} chosen={value} class="tab-edge">
      <div class={`tabs tab-row${narrow ? ' narrow' : ''}`} role="tablist" aria-label={label}>
        {shown.map((t) => {
          const on = t.key === value;
          const words = narrow && t.short ? t.short : t.name;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              class={`tab${on ? ' on' : ''}`}
              aria-selected={on}
              aria-label={words !== t.name ? t.name : undefined}
              aria-description={t.need}
              onClick={() => onPick(t.key)}
            >
              {narrow ? (
                <span class="tab-label">
                  {words}
                  {t.need ? <NeedDot tip={`Needs you: ${t.need}`} /> : null}
                </span>
              ) : (
                <>
                  {t.need ? <NeedDot tip={`Needs you: ${t.need}`} /> : null}
                  {words}
                </>
              )}
            </button>
          );
        })}
        {rest.length ? (
          <span class={`tab-more${inRest ? ' on' : ''}`} role="presentation">
            <PillMenu
              label={inRest ? inRest.short ?? inRest.name : 'More'}
              ariaLabel={inRest ? `More tabs: ${inRest.name}` : 'More tabs'}
              sheetTitle={label}
              class="tab-more-pill"
              choices={rest.map((t) => ({ value: t.key, label: t.name, note: t.need ? `Needs you: ${t.need}` : undefined }))}
              current={inRest ? value : null}
              onPick={onPick}
            />
          </span>
        ) : null}
      </div>
    </EdgeScroll>
  );
}
